import { FieldsTypes, GeneralFieldsName, UserEventFields } from "../consts";
import { fDate } from "./minimal/formatTime.js";
// import NimiTermsOfService from '../assets/docs/nimi-terms-of-services.pdf'
// import NimiPrivacyPolicy from '../assets/docs/nimi-privacy-policy.pdf'
// import MyKindPrivacyPolicy from '../assets/docs/mykind-privacy-policy.pdf'
// import MyKindTermsOfService from '../assets/docs/mykind-terms-of-services.pdf'
import * as Yup from "yup";

export const extractId = (entity) => entity._id

export const eventInputDataToFields = (eventInputData = []) => {
    // Initialize with default field values to prevent undefined errors
    const fields = {
        custom_questions: [],
        birthdayField: { required: false, isHidden: true },
        lnameField: { required: false, isHidden: false },
        fnameField: { required: false, isHidden: false },
        emailField: { required: true, isHidden: false },
        phoneField: { required: true, isHidden: false },
        genderField: { required: false, isHidden: true },
        socialLinkField: { required: false, isHidden: true },
        businessCodeField: { required: false, isHidden: true },
        businessNameField: { required: false, isHidden: true },
        emailMarketingField: { required: false, isHidden: true }
    }
    for (const field of eventInputData) {
        switch (field.name) {
            case GeneralFieldsName.B_DAY:
                fields.birthdayField = field
                break
            case GeneralFieldsName.L_NAME:
                fields.lnameField = field
                break
            case GeneralFieldsName.F_NAME:
                fields.fnameField = field
                break
            case GeneralFieldsName.EMAIL:
                fields.emailField = field
                break
            case GeneralFieldsName.PHONE:
                fields.phoneField = field
                break
            case GeneralFieldsName.GENDER:
                fields.genderField = field
                break
            case GeneralFieldsName.SOCIAL_LINK:
                fields.socialLinkField = field
                // Extract custom validations if they exist
                if (field.customValidations) {
                    fields.socialLinkField = {
                        ...field,
                        customValidations: field.customValidations
                    }
                }
                break
            case GeneralFieldsName.BUSINESS_CODE:
                fields.businessCodeField = field
                break
            case GeneralFieldsName.BUSINESS_NAME:
                fields.businessNameField = field
                break
            case GeneralFieldsName.EMAIL_MARKETING:
                fields.emailMarketingField = field
                break
            default:
                if (field.name?.startsWith('custom_q_')) {
                    fields.custom_questions.push({
                        custom_name: field.name,
                        custom_placeholder: field.placeholder,
                        custom_required: field.required,
                        custom_type: field.type
                    })
                } else {
                    console.log("what is this field?");
                    console.log(field);
                }
        }
    }
    return fields
}

// ============================================
// HELPER FUNCTIONS - Reusable Schema Builders
// ============================================

/**
 * Creates a date field schema that handles empty strings gracefully
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Yup.DateSchema} Date schema with nullable and transform
 */
const createDateField = (isRequired = false) => {
    const baseSchema = Yup.date()
        .nullable()
        .transform((value, originalValue) => {
            // Convert empty string to null to avoid "Invalid Date" error
            return originalValue === "" ? null : value;
        });

    if(isRequired) {
        return baseSchema.required()
    }
    return  baseSchema;
};

/**
 * Creates a basic string field schema
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Yup.StringSchema} String schema with trim
 */
const createStringField = (isRequired = false) => {
    const baseSchema = Yup.string().trim();

    if(isRequired) {
        return baseSchema.required()
    }
    return baseSchema;
};

/**
 * Creates an email field schema (lowercase)
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Yup.StringSchema} Email schema with lowercase transformation
 */
const createEmailField = (isRequired = false) => {
    const baseSchema = Yup.string().trim().lowercase();

    if(isRequired) {
        return baseSchema.required()
    }
    return baseSchema;
};

/**
 * Creates a social link field with optional network validation
 * @param {boolean} isRequired - Whether the field is required
 * @param {object} customValidations - Optional validation rules
 * @returns {Yup.StringSchema} Social link schema with custom network validation
 */
const createSocialLinkField = (isRequired = false, customValidations = null) => {
    let schema = createStringField(isRequired);

    // Add custom network validation if provided
    if (customValidations?.must_contain_one_of_the_following?.length > 0) {
        const allowedNetworks = customValidations.must_contain_one_of_the_following;
        const errorMessage = customValidations.error_message || 'Please provide a valid social network link';

        schema = schema.test(
            'social-network-validation',
            errorMessage,
            function (value) {
                if (!value) return true; // Let required validation handle empty values
                const normalizedValue = value.toLowerCase().trim();
                return allowedNetworks.some(network => normalizedValue.includes(network));
            }
        );
    }

    return schema;
};

/**
 * Creates a custom question field based on type
 * @param {object} customQ - Custom question configuration
 * @param {boolean} isRequired - Whether the field is required
 * @returns {Yup.StringSchema | Yup.DateSchema | Yup.BooleanSchema} Appropriate schema based on custom question type
 */
const createCustomQuestionField = (customQ, isRequired) => {
    switch (customQ.custom_type) {
        case FieldsTypes.STRING:
            return createStringField(isRequired);

        case FieldsTypes.DATE_NO_TIME:
            return createDateField(isRequired);

        case FieldsTypes.YES_OR_NO:
            return isRequired ? Yup.boolean().required() : Yup.boolean();

        default:
            return createStringField(isRequired);
    }
};

/**
 * Adds a field to schema based on field configuration
 * Encapsulates the common pattern of checking required/hidden flags
 *
 * @param {object} schema - Schema object to modify (mutated)
 * @param {object} options - Configuration options
 * @param {string} options.fieldName - Name of the field in schema
 * @param {object} options.fieldConfig - Field configuration from formFields (e.g., fnameField)
 * @param {function} options.schemaBuilder - Function to create the field schema (e.g., createStringField)
 * @param {boolean} [options.forceOptional=false] - Force field to be optional regardless of config (for admin orders)
 */
const addFieldToSchema = (schema, options = {}) => {
    const {
        fieldName,
        fieldConfig,
        schemaBuilder,
        forceOptional = false
    } = options;

    if (!fieldConfig || fieldConfig.isHidden) return;

    const isRequired = !forceOptional && fieldConfig.required;
    schema[fieldName] = schemaBuilder(isRequired);
};

/**
 * Adds social link field to schema with custom network validation
 * Special case of addFieldToSchema that handles customValidations parameter
 *
 * @param {object} schema - Schema object to modify (mutated)
 * @param {object} options - Configuration options
 * @param {object} options.fieldConfig - Social link field configuration
 * @param {boolean} [options.forceOptional=false] - Force field to be optional regardless of config
 */
const addSocialLinkField = (schema, options = {}) => {
    const { fieldConfig, forceOptional = false } = options;
    if (!fieldConfig || fieldConfig.isHidden) return;

    const isRequired = !forceOptional && fieldConfig.required;
    schema[UserEventFields.SOCIAL_LINK] = createSocialLinkField(isRequired, fieldConfig.customValidations);
};

// ============================================
// STRATEGY FUNCTIONS - Order Type Specific Logic
// ============================================

/**
 * Builds schema for PUBLIC orders (customers purchasing tickets)
 * Respects formFields configuration from Production Settings
 */
const buildPublicOrderSchema = (formFields, ignoreCustomQuestions) => {
    const schema = {};
    const { birthdayField, lnameField, fnameField, genderField, socialLinkField, custom_questions, businessCodeField, businessNameField } = formFields;

    // Basic required fields - respect field configuration from API
    addFieldToSchema(schema, {
        fieldName: UserEventFields.FIRST_NAME,
        fieldConfig: fnameField,
        schemaBuilder: createStringField
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.LAST_NAME,
        fieldConfig: lnameField,
        schemaBuilder: createStringField
    });
    schema[UserEventFields.PHONE] = createStringField(true); // always required
    schema[UserEventFields.EMAIL] = createEmailField(true); // always required

    // Optional fields - respect field configuration from API
    addFieldToSchema(schema, {
        fieldName: UserEventFields.BIRTH_DATE,
        fieldConfig: birthdayField,
        schemaBuilder: createDateField
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.GENDER,
        fieldConfig: genderField,
        schemaBuilder: createStringField
    });
    addSocialLinkField(schema, { fieldConfig: socialLinkField });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.BUSINESS_CODE,
        fieldConfig: businessCodeField,
        schemaBuilder: createStringField
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.BUSINESS_NAME,
        fieldConfig: businessNameField,
        schemaBuilder: createStringField
    });

    // Custom questions - respect field configuration from API
    if (custom_questions.length && !ignoreCustomQuestions) {
        for (const customQ of custom_questions) {
            schema[customQ.custom_name] = createCustomQuestionField(customQ, customQ.custom_required);
        }
    }

    return schema;
};

/**
 * Builds schema for MANUAL orders (admin creating orders)
 * Only phone, fname, lname are required - all other fields are optional
 */
const buildAdminOrderSchema = (formFields, ignoreCustomQuestions) => {
    const schema = {};
    const { birthdayField, lnameField, fnameField, genderField, socialLinkField, custom_questions, businessCodeField, businessNameField } = formFields;

    // Required fields - respect field configuration from API
    addFieldToSchema(schema, {
        fieldName: UserEventFields.FIRST_NAME,
        fieldConfig: fnameField,
        schemaBuilder: createStringField
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.LAST_NAME,
        fieldConfig: lnameField,
        schemaBuilder: createStringField
    });
    schema[UserEventFields.PHONE] = createStringField(true); // always required
    // Everything else is optional (forceOptional = true ignores API config)
    schema[UserEventFields.EMAIL] = createEmailField(false); // always optional for admin orders
    addFieldToSchema(schema, {
        fieldName: UserEventFields.BIRTH_DATE,
        fieldConfig: birthdayField,
        schemaBuilder: createDateField,
        forceOptional: true
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.GENDER,
        fieldConfig: genderField,
        schemaBuilder: createStringField,
        forceOptional: true
    });
    addSocialLinkField(schema, {
        fieldConfig: socialLinkField,
        forceOptional: true  // optional but still validates networks
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.BUSINESS_CODE,
        fieldConfig: businessCodeField,
        schemaBuilder: createStringField,
        forceOptional: true
    });
    addFieldToSchema(schema, {
        fieldName: UserEventFields.BUSINESS_NAME,
        fieldConfig: businessNameField,
        schemaBuilder: createStringField,
        forceOptional: true
    });

    // Custom questions are always optional for admin orders
    if (custom_questions.length && !ignoreCustomQuestions) {
        for (const customQ of custom_questions) {
            schema[customQ.custom_name] = createCustomQuestionField(customQ, false);
        }
    }

    return schema;
};

// ============================================
// MAIN FUNCTION - Strategy Selector
// ============================================

/**
 * Builds Yup validation schema for user event forms
 * Delegates to appropriate strategy based on order type
 *
 * @param {object} formFields - Field configuration from Production Settings
 * @param {boolean} ignoreCustomQuestions - Whether to skip custom questions
 * @param {boolean} isAdminOrder - Whether this is an admin-created order
 * @returns {object} Yup schema object
 */
export const buildUserEventSchemaByEventInputData = (formFields, ignoreCustomQuestions = false, isAdminOrder = false) => {
    if(isAdminOrder) {
        return buildAdminOrderSchema(formFields, ignoreCustomQuestions);
    }

    return buildPublicOrderSchema(formFields, ignoreCustomQuestions);
}

export const extractCustomQuestionsFromEventInputData = (eventInputData) => {
    const results = []
    for (const inputData of eventInputData) {
        if (inputData.name.startsWith('custom_q')) {
            results.push(inputData)
        }
    }
    return results
}

export const transformCustomQuestionValueByType = (customQuestion, value, translate) => {
    if (!value) {
        return "-"
    }
    switch (customQuestion.type) {
        case FieldsTypes.DATE_NO_TIME:
            return fDate(value)
        case FieldsTypes.YES_OR_NO:
            return value === "1" ? translate('transformers.yes') : translate('transformers.no')
        case FieldsTypes.STRING:
            return value
    }
}

// Prepare number for whatsApp format
export const normalizePhoneNumber = (number) => {
    if (number.startsWith('0') || number.startsWith('+')) {
        return number.slice(1, 1) + number.slice(1, number.length)
    }
}



export const getRelevantPaymentFormError = (e, translate) => {
    // Safely access PayMe fields (may not exist for Stripe payments)
    const paymeFields = window.PayMe?.fields || {};
    const { EXPIRATION, NUMBER, CVC, NAME_FIRST, NAME_LAST } = paymeFields;

    let errorToRender
    // setting a default error message in case we don't have a specific one to show
    // Check for user-facing message first, but only use if it's not empty
    if(e?.details?.status === 401) {
        errorToRender = translate('event_page.unauthorised_payment_form_error')
        return errorToRender
    }

    if (e?.userFacingMessage && e.userFacingMessage.trim() !== '') {
        errorToRender = e.userFacingMessage
    } else if (e?.payload?.status_error_details) {
        errorToRender = e.payload.status_error_details
    } else if (e?.message && typeof e.message === 'string') {
        // Only use e.message if it's a string (not an object)
        errorToRender = e.message
    } else {
        // Fallback to generic error
        errorToRender = translate('event_page.generic_payment_form_error')
    }
    if (e?.message) {
        if (e.message === "Invalid phone number") {
            errorToRender = translate('event_page.payment_invalid_phone_number')
            return errorToRender
        }
        if (e.message.includes("`email` is required")) {
            errorToRender = translate('event_page.payment_invalid_email')
            return errorToRender
        }
        if (e.message.includes("seats already taken")) {
            /* this is the server error message when the seats are already taken  
            `seats already taken (${seatsTaken.toString()}) in order number: ${JSON.stringify(
                   eventOrderNumbers
               )} for event: ${event._id.toString()}`
            */
            const seatsTaken = e.message.split("(")[1].split(")")[0]
            errorToRender = translate('event_page.seats_already_taken') + ` ${seatsTaken}`
            // check if current url contains the /dashboard/ path if so we need to show a different message
            if (window.location.pathname.includes('/dashboard/')) {
                const eventOrderNumbers = e.message.split("order number: ")[1].split(" for event:")[0]
                errorToRender += ` ${translate('transformers.order_number_prefix')} ${eventOrderNumbers}`
            }
            return errorToRender
        }
    }
    // Mean we had an error with the 'PayMe'
    if (e?.type === 'tokenize-error') {
        // This is errors from PayMe api (after form submitted)
        if (e.payload) {
            if (e.payload.status_error_details === "Invalid credit card expiry") {
                errorToRender = translate('event_page.invalid_card_expiry')
                return errorToRender
            }
            if (e.payload.status_error_details === "Incorrect card number") {
                errorToRender = translate('event_page.invalid_card_number')
                return errorToRender
            }
            if (e.payload.status_error_details === "Invalid credit card CVV") {
                errorToRender = translate('event_page.invalid_card_cvv')
                return errorToRender
            }
            if (e.payload.status_error_details && e.payload.status_error_details.toLowerCase().includes("refusal by credit company")) {
                errorToRender = translate('event_page.refusal_payment')
                return errorToRender
            }
            if (e.payload.status_error_details && e.payload.status_error_details.toLowerCase().includes("internal server error")) {
                errorToRender = translate('event_page.payment_provider_internal_error')
                return errorToRender
            }
            if (e.payload.status_error_details) {
                errorToRender = e.payload.status_error_details
                return errorToRender
            }
        }
        // This is errors from PayMe sdk (before form submitted)
        if (e.errors) {
            if (e.errors[EXPIRATION]) {
                errorToRender = translate('event_page.missing_fields')
            }
            if (e.errors[NUMBER]) {
                errorToRender = translate('event_page.missing_fields')
            }
            if (e.errors[CVC]) {
                errorToRender = translate('event_page.missing_fields')
            }
            if (e.errors[NAME_FIRST]) {
                errorToRender = translate('event_page.missing_fields')
            }
            if (e.errors[NAME_LAST]) {
                errorToRender = translate('event_page.missing_fields')
            }
        }
    }
    return errorToRender
}

export const getRelevantHrcError = (e, translate) => {
    let message = e.message ? e.message.toLowerCase() : ""
    if (message) {
        if (message.includes("already used for this event")) {
            return translate('transformers.hrc_errors.subscription_already_used')
        }
        if (message.includes("season ticket not active or limit is reached")) {
            return translate('transformers.hrc_errors.subscription_not_active')
        }
        if (message.includes("end user season ticket limit reached for this event")) {
            return translate('transformers.hrc_errors.no_entries_left_for_event')
        }
        if (message.includes("end user season ticket limit reached")) {
            return translate('transformers.hrc_errors.no_entries_left')
        }
        if (message.includes("end user season ticket not configured for this event")) {
            return translate('transformers.hrc_errors.not_configured_for_event')
        }

        if (message.includes("end user season ticket not found")) {
            return translate('transformers.hrc_errors.end_user_season_ticket_not_found')
        }
        return translate(e.message)
    }
    return translate('transformers.hrc_errors.subscription_not_found')
}
